import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Calculator, ChevronRight, Check, AlertTriangle, Clock, Phone, Mail, MessageCircle, Zap, Users, HandshakeIcon, FileCheck, ShieldCheck, Headphones, CreditCard, Cog } from 'lucide-react';
import { ConsultingLayout } from '../layouts/ConsultingLayout';
import { Footer } from '../components/legal';
import { Navigation } from '../components/Navigation';
import { LeistungenCarousel } from '../components/LeistungenCarousel';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';

/**
 * ConsultingHome - "Der Finanzfahrplan"
 * 
 * Fokus: §34d GewO (Versicherungsvermittler)
 * - BU, Risikoleben, private Rentenversicherung/Fondspolicen
 * - KEINE ETF-Depots, Baufinanzierung, Immobilien, Rendite-Prognosen
 * - Du-Ansprache durchgehend
 */

const ConsultingHome = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);

  const openCalendly = () => {
    navigate('/strategiegespraech');
  };

  // Parallax effect for hero
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const scrolled = window.scrollY;
        heroRef.current.style.transform = `translateY(${scrolled * 0.3}px)`;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <ConsultingLayout>
      {/* Navigation */}
      <Navigation variant="dark" />

      {/* Hero Section - Der Finanzfahrplan */}
      <section className="relative min-h-screen bg-navy overflow-hidden">
        {/* Ambient Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy to-[#0d1225]" />
          
          {/* Subtle glow effects */}
          <div className="absolute top-1/4 left-0 w-[800px] h-[800px] bg-cobalt/[0.05] rounded-full blur-[200px]" />
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-cobalt/[0.03] rounded-full blur-[200px]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-24 sm:pt-28 lg:pt-32 pb-16 lg:pb-24">
          {/* Main Grid - 2 Columns on Desktop */}
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-16 items-center lg:min-h-[calc(100vh-200px)]">
            
            {/* Left Column - Text Content */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="order-1 lg:order-1 relative z-10"
            >
              {/* Headline */}
              <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight mb-6 sm:mb-8">
                <span className="block mb-2">Nicht sicher, ob du richtig abgesichert bist?</span>
                <span className="block text-white/90 text-2xl sm:text-3xl lg:text-4xl">
                  Dein Vertragscheck in 48 Stunden.
                </span>
                <span className="block text-white/50 text-xl sm:text-2xl lg:text-3xl mt-2">
                  Unabhängig, verständlich, durchgerechnet.
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-base sm:text-lg text-white/60 leading-relaxed mb-6 max-w-lg">
                Absicherung, Altersvorsorge und oft mehr Geld im Monat, als du denkst. Besonders, wenn seit Jahren niemand mehr draufgeschaut hat.
              </p>
              
              {/* Trust Points - Vertical List */}
              <div className="flex flex-col gap-2.5 mb-6 lg:mb-8">
                {[
                  '§34d-zugelassen (IHK)',
                  'Anbieterunabhängig',
                  'Erstanalyse kostenlos',
                  'Ingenieur-Background',
                ].map((point, i) => (
                  <div 
                    key={i}
                    className="flex items-center gap-2.5 text-base sm:text-lg text-white/80"
                  >
                    <Check className="w-5 h-5 text-emerald flex-shrink-0" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>

              {/* Mobile Photo - Between Trust Points and CTAs */}
              <div className="flex justify-center mb-6 lg:hidden">
                <div className="relative">
                  <div className="w-48 h-60 sm:w-56 sm:h-72 rounded-2xl overflow-hidden border-2 border-white/10 shadow-2xl">
                    <img 
                      src="/images/nico-hero-v2.jpg" 
                      alt="Nico Helfrich"
                      className="w-full h-full object-cover object-top"
                      loading="eager"
                    />
                  </div>
                  
                  {/* Subtle gradient overlay at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-navy/60 to-transparent rounded-b-2xl" />
                  
                  {/* IHK Zertifikat */}
                  <a 
                    href="https://www.vermittlerregister.info/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute -bottom-3 -right-2 sm:-bottom-4 sm:-right-3 w-20 sm:w-24 rounded-lg shadow-xl hover:shadow-2xl transition-all z-10 bg-white p-1 border border-black/10 transform rotate-6"
                  >
                    <img 
                      src="/logos/ihk-zertifikat-small.png" 
                      alt="IHK Zertifikat §34d GewO"
                      className="w-full h-auto rounded"
                    />
                  </a>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button 
                  onClick={openCalendly}
                  className="group flex items-center justify-center gap-2 px-5 sm:px-6 py-3 bg-white text-navy rounded-lg font-medium text-sm hover:bg-bone transition-all duration-300"
                  data-testid="hero-cta-primary"
                >
                  Erstgespräch vereinbaren
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <button 
                  onClick={() => navigate('/absicherungscheck')}
                  className="group flex items-center justify-center gap-2 px-5 sm:px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg font-medium text-sm hover:bg-white/20 transition-all duration-300"
                  data-testid="hero-cta-secondary"
                >
                  Absicherungs-Check starten
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
            
            {/* Right Column - Photo (Desktop only) */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="hidden lg:flex order-2 justify-center"
              ref={heroRef}
            >
              <div className="relative">
                <div className="w-96 h-[480px] rounded-2xl overflow-hidden border-2 border-white/10 shadow-2xl">
                  <img 
                    src="/images/nico-hero-v2.jpg" 
                    alt="Nico Helfrich - Finanzstrategie"
                    className="w-full h-full object-cover object-top"
                    loading="eager"
                  />
                </div>
                
                {/* Subtle gradient overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-navy/60 to-transparent rounded-b-2xl" />
                
                {/* IHK Zertifikat - Bottom Right, overlapping the photo with 3D tilt */}
                <a 
                  href="https://www.vermittlerregister.info/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute -bottom-8 -right-6 w-40 rounded-lg shadow-xl hover:shadow-2xl transition-all z-10 bg-white p-1 border border-black/10 transform rotate-6 hover:rotate-3 hover:scale-105"
                  style={{ transformStyle: 'preserve-3d', perspective: '1000px' }}
                >
                  <img 
                    src="/logos/ihk-zertifikat-small.png" 
                    alt="IHK Zertifikat §34d GewO"
                    className="w-full h-auto rounded"
                  />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner Logos - Scrolling Marquee (direkt unter Hero) */}
      <section className="py-8 bg-white border-b border-black/5 overflow-hidden">
        <div className="px-4 sm:px-6 md:px-8">
          <p className="text-xs text-slate text-center mb-4 uppercase tracking-wider">
            Partner
          </p>
          
          {/* Marquee Container */}
          <div className="relative w-full overflow-hidden">
            <div 
              className="flex w-max"
              style={{
                animation: 'marquee-scroll 180s linear infinite',
              }}
            >
              {/* Logos repeated 3x for seamless loop */}
              {[...Array(3)].map((_, setIndex) => (
                <div key={setIndex} className="flex items-center">
                  {[
                    { name: 'Allianz', src: '/logos/allianz.png' },
                    { name: 'AXA', src: '/logos/axa.png' },
                    { name: 'ARAG', src: '/logos/arag.png' },
                    { name: 'Adam Riese', src: '/logos/adamriese.png' },
                    { name: 'Barmenia', src: '/logos/barmenia.png' },
                    { name: 'DBV', src: '/logos/dbv.png' },
                    { name: 'myLife', src: '/logos/mylife.gif' },
                    { name: 'Baloise', src: '/logos/baloise.png' },
                    { name: 'HDI', src: '/logos/hdi.png' },
                    { name: 'VHV', src: '/logos/vhv.png' },
                    { name: 'Provinzial', src: '/logos/provinzial.png' },
                    { name: 'HUK-Coburg', src: '/logos/huk.png' },
                    { name: 'Helvetia', src: '/logos/helvetia.png' },
                    { name: 'uniVersa', src: '/logos/universa.webp' },
                    { name: 'Volkswohl Bund', src: '/logos/volkswohlbund.webp' },
                    { name: 'Waldenburger', src: '/logos/waldenburger.png' },
                    { name: 'Württembergische', src: '/logos/wuerttembergische.png' },
                    { name: 'Zurich', src: '/logos/zurich.png' },
                    { name: 'HanseMerkur', src: '/logos/hansemerkur.png' },
                    { name: 'InterRisk', src: '/logos/interrisk.png' },
                    { name: 'LV 1871', src: '/logos/lv1871.png' },
                    { name: 'Nürnberger', src: '/logos/nuernberger.webp' },
                    { name: 'Signal Iduna', src: '/logos/signaliduna.png' },
                    { name: 'die Bayerische', src: '/logos/diebayerische.webp' },
                    { name: 'DKV', src: '/logos/dkv.png' },
                    { name: 'ERGO', src: '/logos/ergo.webp' },
                    { name: 'Hallesche', src: '/logos/hallesche.png' },
                    { name: 'Hannoversche', src: '/logos/hannoversche.webp' },
                    { name: 'Continentale', src: '/logos/continentale.png' },
                    { name: 'R+V', src: '/logos/rv.png' },
                    { name: 'Alte Leipziger', src: '/logos/alteleipziger.png' },
                    { name: 'Ammerländer', src: '/logos/ammerlaender.png' },
                    { name: 'Canada Life', src: '/logos/canadalife.png' },
                  ].map((logo, i) => (
                    <div 
                      key={`${setIndex}-${i}`}
                      className="flex-shrink-0 flex items-center justify-center w-[140px] sm:w-[160px] h-12 mx-2"
                    >
                      <img 
                        src={logo.src} 
                        alt={logo.name}
                        className="max-h-[40px] max-w-[120px] w-auto h-auto object-contain opacity-70"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          
          <style>{`
            @keyframes marquee-scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-33.333%); }
            }
          `}</style>
        </div>
      </section>

      {/* Warum ich - Bento Grid Style */}
      <section className="py-20 md:py-28 bg-ice">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-10">
            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold text-navy mb-3">
              Warum du mit mir die beste Wahl triffst
            </h2>
            <p className="text-base sm:text-lg text-charcoal max-w-2xl mx-auto">
              6 starke Gründe, warum sich die Zusammenarbeit mit mir als unabhängiger Makler lohnt.
            </p>
          </div>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Row 1 - 4 Cards */}
            <div className="bg-white rounded-2xl p-6 border border-black/5">
              <div className="w-12 h-12 rounded-xl bg-ice flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-navy" strokeWidth={1.5} />
              </div>
              <h3 className="font-outfit font-bold text-navy text-lg mb-2">Vertragscheck in 48 Stunden</h3>
              <p className="text-sm text-charcoal leading-relaxed">
                Du bekommst innerhalb von 48 Stunden eine vollständige Einschätzung deiner bestehenden Verträge.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 border border-black/5">
              <div className="w-12 h-12 rounded-xl bg-ice flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-navy" strokeWidth={1.5} />
              </div>
              <h3 className="font-outfit font-bold text-navy text-lg mb-2">Richtige Deckung im Ernstfall</h3>
              <p className="text-sm text-charcoal leading-relaxed">
                Ich prüfe, ob im Schadensfall auch wirklich gezahlt wird, statt an der falschen Stelle gespart zu haben.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 border border-black/5">
              <div className="w-12 h-12 rounded-xl bg-ice flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-navy" strokeWidth={1.5} />
              </div>
              <h3 className="font-outfit font-bold text-navy text-lg mb-2">Unabhängig & neutral</h3>
              <p className="text-sm text-charcoal leading-relaxed">
                Keine Zusammenarbeit mit nur einem Anbieter. 100% objektive Bewertung in deinem Sinne.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 border border-black/5">
              <div className="w-12 h-12 rounded-xl bg-ice flex items-center justify-center mb-4">
                <Headphones className="w-6 h-6 text-navy" strokeWidth={1.5} />
              </div>
              <h3 className="font-outfit font-bold text-navy text-lg mb-2">Schnelle persönliche Erreichbarkeit</h3>
              <p className="text-sm text-charcoal leading-relaxed">
                Du erreichst mich direkt. Ohne Hotline, ohne Wartezeit. Auch abends oder am Wochenende bin ich für dich da.
              </p>
            </div>
            
            {/* Row 2 - CTA Card + 2 Cards */}
            <div className="bg-navy rounded-2xl p-6 sm:col-span-2 flex flex-col justify-between min-h-[200px]">
              <div>
                <h3 className="font-outfit font-bold text-white text-xl sm:text-2xl mb-3">In nur wenigen Minuten zur Anfrage</h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  Du brauchst eine Einschätzung oder eine Beratung? Mit nur wenigen Klicks kannst du direkt Kontakt aufnehmen und erhältst schnell eine Rückmeldung.
                </p>
              </div>
              <button 
                onClick={openCalendly}
                className="mt-4 inline-flex items-center gap-2 px-5 py-3 bg-white hover:bg-bone text-navy rounded-lg font-medium text-sm transition-all w-fit"
              >
                Jetzt Termin vereinbaren
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            
            <div className="bg-white rounded-2xl p-6 border border-black/5">
              <div className="w-12 h-12 rounded-xl bg-ice flex items-center justify-center mb-4">
                <CreditCard className="w-6 h-6 text-navy" strokeWidth={1.5} />
              </div>
              <h3 className="font-outfit font-bold text-navy text-lg mb-2">Faire Abrechnung</h3>
              <p className="text-sm text-charcoal leading-relaxed">
                Keine Vorkasse nötig. Die Vergütung läuft über die Versicherungsgesellschaften. Keine Extrakosten für dich.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 border border-black/5">
              <div className="w-12 h-12 rounded-xl bg-ice flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-navy" strokeWidth={1.5} />
              </div>
              <h3 className="font-outfit font-bold text-navy text-lg mb-2">Digital & effizient</h3>
              <p className="text-sm text-charcoal leading-relaxed">
                Von der Terminvereinbarung bis zur Vertragsübermittlung läuft alles digital und ohne Papierchaos. Deine Verträge verwaltest du übersichtlich in einer App.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leistungen Section - New Layout */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
          {/* Top: Title + Intro left, Checklist right */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-12">
            <div>
              <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4 leading-tight">
                Diese Leistungen bekommst du von mir
              </h2>
              <p className="text-base text-charcoal leading-relaxed">
                Als unabhängiger Makler bin ich für dich da, von der ersten Analyse bis zur laufenden Betreuung danach.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {[
                'Oft über 100 € mehr im Monat',
                'Sicherheit, dass im Ernstfall gezahlt wird',
                'Keine böse Überraschung im Alter',
                'Kein Papierkram für dich',
                'Nicht allein, wenn es zählt',
                'Deine Absicherung wächst mit dir',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded bg-navy flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" strokeWidth={2.5} />
                  </div>
                  <span className="text-sm sm:text-base text-navy">{item}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Bottom: 6 Feature Cards Carousel */}
          <LeistungenCarousel />
        </div>
      </section>

      {/* Der Prozess - So arbeiten wir zusammen */}
      <section className="py-20 md:py-28 bg-ice relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cobalt/[0.02] to-transparent" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 relative">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate mb-3">So arbeiten wir zusammen</div>
            <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-navy tracking-tight mb-4">
              Der Prozess
            </h2>
            <p className="text-base text-charcoal max-w-xl mx-auto">
              Ein strukturierter Ablauf, von der Analyse bis zur Umsetzung.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { 
                step: '01', 
                title: 'Verstehen', 
                desc: 'Im kostenlosen Erstgespräch (ca. 30 Min, Video-Call) lernen wir uns kennen. Ich will wissen, wo du stehst und wo du hinwillst.',
                detail: 'Rückmeldung innerhalb von 24h',
                color: 'cobalt'
              },
              { 
                step: '02', 
                title: 'Strukturieren', 
                desc: 'Ich erstelle deinen Vertragscheck (innerhalb von 48h) und entwickle mit dir den Finanzfahrplan: Was brauchst du wirklich?',
                detail: 'Per Video-Call oder Telefon',
                color: 'emerald'
              },
              { 
                step: '03', 
                title: 'Begleiten', 
                desc: 'Ich setze mit dir um und bleibe dran. Bei Fragen erreichst du mich direkt per Telefon, E-Mail oder WhatsApp.',
                detail: 'Dauer richtet sich nach dir',
                color: 'navy'
              }
            ].map((item, i) => (
              <div 
                key={i} 
                className="group relative bg-white rounded-2xl p-6 md:p-8 border border-black/5 hover:border-black/10 hover:shadow-xl hover:shadow-navy/5 transition-all duration-500 cursor-pointer"
                onClick={() => navigate('/strategiegespraech')}
                data-testid={`process-card-${item.step}`}
              >
                {/* Step Number */}
                <div className="absolute -top-3 -right-3 w-10 h-10 sm:w-12 sm:h-12 bg-bone rounded-full flex items-center justify-center border border-black/5 shadow-sm">
                  <span className="font-mono text-xs sm:text-sm font-bold text-navy">{item.step}</span>
                </div>
                
                {/* Icon placeholder via color */}
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center mb-5 ${
                  item.color === 'cobalt' ? 'bg-cobalt/10' :
                  item.color === 'emerald' ? 'bg-emerald/10' : 'bg-navy/10'
                }`}>
                  <span className={`font-mono text-xl font-bold ${
                    item.color === 'cobalt' ? 'text-cobalt' :
                    item.color === 'emerald' ? 'text-emerald' : 'text-navy'
                  }`}>{item.step}</span>
                </div>
                
                {/* Content */}
                <h3 className="font-outfit text-lg sm:text-xl font-bold text-navy mb-2">{item.title}</h3>
                <p className="text-sm text-charcoal leading-relaxed mb-2">{item.desc}</p>
                <p className="text-xs text-cobalt font-medium">{item.detail}</p>
                
                {/* Hover Arrow */}
                <div className="mt-5 flex items-center gap-2 text-slate group-hover:text-cobalt transition-colors">
                  <span className="text-sm font-medium">Termin vereinbaren</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kostenlos-Hinweis */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 text-center">
          <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-6 leading-tight">
            Kostet dich nichts. Bringt dir alles.
          </h2>
          <p className="text-base sm:text-lg text-charcoal leading-relaxed max-w-2xl mx-auto">
            Von der ersten Analyse bis zur laufenden Betreuung zahlst du nie etwas an mich. Als unabhängiger Makler bin ich an keinen Anbieter gebunden. Meine Vergütung kommt von der Versicherungsgesellschaft, transparent offengelegt, wie gesetzlich vorgeschrieben. Egal wo und bei wem du am Ende abschließt: Für dich entstehen dadurch keine Mehrkosten.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-ice">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 md:mb-12">
            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate mb-3">Häufige Fragen</div>
            <h2 className="font-playfair text-xl sm:text-2xl md:text-3xl font-bold text-navy tracking-tight">
              Das werde ich oft gefragt
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-3">
            <AccordionItem value="faq-1" className="bg-white rounded-xl border border-black/5 shadow-sm px-5 data-[state=open]:shadow-md transition-shadow">
              <AccordionTrigger className="hover:no-underline py-5">
                <span className="font-outfit text-sm sm:text-base font-semibold text-navy text-left pr-4">
                  Was kostet mich die Beratung?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-xs sm:text-sm text-charcoal leading-relaxed pb-5">
                Nichts. Meine Vergütung kommt von der Versicherungsgesellschaft, nicht von dir. Es entstehen für dich keine Mehrkosten, egal wo und bei wem du am Ende abschließt.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="faq-2" className="bg-white rounded-xl border border-black/5 shadow-sm px-5 data-[state=open]:shadow-md transition-shadow">
              <AccordionTrigger className="hover:no-underline py-5">
                <span className="font-outfit text-sm sm:text-base font-semibold text-navy text-left pr-4">
                  Wie läuft das Erstgespräch ab?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-xs sm:text-sm text-charcoal leading-relaxed pb-5">
                Wir lernen uns 15 Minuten per Video-Call kennen. Ich höre mir deine Situation an, stelle ein paar Fragen und gebe dir eine erste Einschätzung. Danach entscheidest du, ob wir weitermachen.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="faq-3" className="bg-white rounded-xl border border-black/5 shadow-sm px-5 data-[state=open]:shadow-md transition-shadow">
              <AccordionTrigger className="hover:no-underline py-5">
                <span className="font-outfit text-sm sm:text-base font-semibold text-navy text-left pr-4">
                  Was, wenn ich schon Verträge habe?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-xs sm:text-sm text-charcoal leading-relaxed pb-5">
                Dann schauen wir sie uns an. Manchmal sind bestehende Verträge gut, dann optimieren wir drumherum. Manchmal sind sie nicht mehr passend, dann besprechen wir, ob ein Wechsel Sinn macht.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="faq-4" className="bg-white rounded-xl border border-black/5 shadow-sm px-5 data-[state=open]:shadow-md transition-shadow">
              <AccordionTrigger className="hover:no-underline py-5">
                <span className="font-outfit text-sm sm:text-base font-semibold text-navy text-left pr-4">
                  Warum sollte ich nicht direkt online abschließen?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-xs sm:text-sm text-charcoal leading-relaxed pb-5">
                Online-Vergleiche zeigen nur den Preis, nicht die Bedingungen. Bei BU-Versicherungen steckt der Unterschied im Detail: Welche Berufe sind versichert? Wie ist die Nachversicherung geregelt? Das kläre ich mit dir, bevor du unterschreibst.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="faq-5" className="bg-white rounded-xl border border-black/5 shadow-sm px-5 data-[state=open]:shadow-md transition-shadow">
              <AccordionTrigger className="hover:no-underline py-5">
                <span className="font-outfit text-sm sm:text-base font-semibold text-navy text-left pr-4">
                  Wie lange dauert der ganze Prozess?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-xs sm:text-sm text-charcoal leading-relaxed pb-5">
                Das hängt ganz von dir ab. Manche wollen schnell Klarheit und sind in 2 Wochen durch. Andere nehmen sich mehr Zeit für Entscheidungen. Ich passe mich deinem Tempo an.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="faq-6" className="bg-white rounded-xl border border-black/5 shadow-sm px-5 data-[state=open]:shadow-md transition-shadow">
              <AccordionTrigger className="hover:no-underline py-5">
                <span className="font-outfit text-sm sm:text-base font-semibold text-navy text-left pr-4">
                  Was passiert mit meinen Daten?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-xs sm:text-sm text-charcoal leading-relaxed pb-5">
                Deine Daten bleiben bei mir und werden nicht an Dritte verkauft. Ich nutze sie ausschließlich für deine Beratung. Falls wir nicht zusammenarbeiten, lösche ich sie auf Wunsch vollständig.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Abschluss-Sektion mit CTA */}
      <section className="py-16 md:py-24 bg-navy">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 text-center">
          <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
            Versicherung zahlt nicht? Ich helfe sofort, egal welche Versicherung betroffen ist.
          </h2>
          <p className="text-base sm:text-lg text-white/70 leading-relaxed mb-8 max-w-2xl mx-auto">
            Viele merken erst im Schadensfall, ob ihre Versicherung wirklich zahlt, ob bei BU, Kfz, Hausrat oder Haftpflicht. Ich schätze deine Situation unabhängig ein und unterstütze dich bei der Durchsetzung, auch wenn du noch nicht mein Kunde bist.
          </p>
          <button 
            onClick={() => navigate('/strategiegespraech')}
            className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-navy rounded-lg font-medium text-base hover:bg-bone transition-all duration-300"
          >
            Jetzt Kontakt aufnehmen
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <Footer variant="dark" />
    </ConsultingLayout>
  );
};

export default ConsultingHome;
